/**
 * Onboarding Lifecycle — Sprint A.1
 * ──────────────────────────────────
 * Canonical lifecycle projection over OnboardingSession + InviteToken.
 *
 * OnboardingSession is the source of truth for the contractor onboarding
 * workflow. Contractor records are business entities — they do NOT drive
 * lifecycle state.
 *
 * Hard rules:
 *   • Pure derivation. No writes. No mutation of contractor.onboardingStatus.
 *   • Single canonical stage enum used by every onboarding surface.
 *   • `Contractor.onboardingStatus` remains for legacy intake/business-entity
 *     management but MUST NOT be interpreted as session lifecycle.
 */

import type {
  OnboardingInviteToken,
  OnboardingSession,
} from "./types";
import { isInviteTokenUsable } from "./inviteTokens";

export type OnboardingLifecycleStage =
  | "invited"
  | "in_progress"
  | "submitted"
  | "reupload_requested"
  | "under_review"
  | "approved"
  | "rejected"
  | "expired"
  | "withdrawn";

export const ONBOARDING_LIFECYCLE_STAGES: OnboardingLifecycleStage[] = [
  "invited",
  "in_progress",
  "submitted",
  "reupload_requested",
  "under_review",
  "approved",
  "rejected",
  "expired",
  "withdrawn",
];

export const ONBOARDING_LIFECYCLE_LABELS: Record<OnboardingLifecycleStage, string> = {
  invited: "Invited",
  in_progress: "In Progress",
  submitted: "Submitted",
  reupload_requested: "Reupload Requested",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  withdrawn: "Withdrawn",
};

export interface OnboardingLifecycleRow {
  id: string;
  stage: OnboardingLifecycleStage;
  tokenId: string;
  sessionId?: string;
  contractorId?: string;
  entityType: OnboardingSession["entityType"];
  parentEntityId?: string;
  label: string;
  lastTouchedAt: string;
}

function tokenLabel(t: OnboardingInviteToken): string {
  return `${t.entityType} invite · ${t.id.slice(-6)}`;
}

function sessionLabel(s: OnboardingSession): string {
  const name = (s.draft?.name ?? "").trim();
  return name || `${s.entityType} session · ${s.id.slice(-6)}`;
}

function isDeletedSession(session: OnboardingSession): boolean {
  return Boolean(session.draft?.deletedAt);
}

function isDeletedToken(token: OnboardingInviteToken): boolean {
  return String(token.revokedReason ?? "").startsWith("[deleted]");
}

/**
 * Project an OnboardingSession to its canonical lifecycle stage.
 * `submitted` and `under_review` collapse to `under_review` once the
 * admin queue picks it up; until that signal exists we keep them
 * distinct so the invitee sees "Submitted" and the admin sees "Under
 * Review" on the same record.
 */
export function deriveSessionLifecycleStage(
  session: OnboardingSession,
  perspective: "invitee" | "admin" = "admin",
): OnboardingLifecycleStage {
  if (session.draft?.archivedAt) return "withdrawn";
  switch (session.status) {
    case "draft":
      return "in_progress";
    case "submitted":
      return perspective === "invitee" ? "submitted" : "under_review";
    case "reupload_requested":
      return "reupload_requested";
    case "approved":
      return "approved";
    case "rejected":
      return "rejected";
    default:
      return "in_progress";
  }
}

/** Token (no session yet) → invited | expired | withdrawn. */
export function deriveTokenLifecycleStage(
  t: OnboardingInviteToken,
  at: string = new Date().toISOString(),
): OnboardingLifecycleStage {
  if (t.status === "revoked") return "withdrawn";
  if (t.status === "expired") return "expired";
  if (t.status === "pending" && !isInviteTokenUsable(t, at)) return "expired";
  return "invited";
}

/**
 * Build a canonical lifecycle row list. Sessions take precedence over
 * their source token (a consumed token surfaces as its session).
 * Sorted by lastTouchedAt descending.
 */
export function getOnboardingLifecycleRows(
  tokens: OnboardingInviteToken[],
  sessions: OnboardingSession[],
  opts: { perspective?: "invitee" | "admin"; at?: string } = {},
): OnboardingLifecycleRow[] {
  const at = opts.at ?? new Date().toISOString();
  const perspective = opts.perspective ?? "admin";
  const sessionByTokenId = new Map<string, OnboardingSession>();
  for (const s of sessions) sessionByTokenId.set(s.tokenId, s);

  const rows: OnboardingLifecycleRow[] = [];
  for (const t of tokens) {
    if (isDeletedToken(t)) continue;
    const linked = sessionByTokenId.get(t.id);
    if (linked) {
      if (isDeletedSession(linked)) continue;
      rows.push({
        id: `session:${linked.id}`,
        stage: deriveSessionLifecycleStage(linked, perspective),
        tokenId: t.id,
        sessionId: linked.id,
        contractorId: linked.contractorId,
        entityType: linked.entityType,
        parentEntityId: linked.parentEntityId,
        label: sessionLabel(linked),
        lastTouchedAt: linked.lastTouchedAt,
      });
      continue;
    }
    rows.push({
      id: `token:${t.id}`,
      stage: deriveTokenLifecycleStage(t, at),
      tokenId: t.id,
      contractorId: t.contractorId,
      entityType: t.entityType,
      parentEntityId: t.parentId,
      label: tokenLabel(t),
      lastTouchedAt: t.createdAt,
    });
  }

  return rows.sort((a, b) =>
    a.lastTouchedAt < b.lastTouchedAt ? 1 : a.lastTouchedAt > b.lastTouchedAt ? -1 : 0,
  );
}

/** Count rows per stage. Useful for KPI strips. */
export function getOnboardingLifecycleStageCounts(
  rows: OnboardingLifecycleRow[],
): Record<OnboardingLifecycleStage, number> {
  const counts = {
    invited: 0, in_progress: 0, submitted: 0, reupload_requested: 0,
    under_review: 0, approved: 0, rejected: 0, expired: 0, withdrawn: 0,
  } as Record<OnboardingLifecycleStage, number>;
  for (const r of rows) counts[r.stage] += 1;
  return counts;
}

/**
 * Canonical user-facing label for a raw OnboardingSession status.
 * Use this anywhere a session.status is rendered so the wording stays
 * aligned with `ONBOARDING_LIFECYCLE_LABELS`. Defaults to the admin
 * perspective (submitted → "Under Review"). Pass perspective="invitee"
 * for the wizard's own status surfacing.
 */
export function getCanonicalSessionStatusLabel(
  session: Pick<OnboardingSession, "status">,
  perspective: "invitee" | "admin" = "admin",
): string {
  const stage = deriveSessionLifecycleStage(session as OnboardingSession, perspective);
  return ONBOARDING_LIFECYCLE_LABELS[stage];
}

/**
 * Humanize a legacy `Contractor.onboardingStatus` value for display.
 * This field is NOT canonical lifecycle (see file header) but legacy
 * surfaces still render it; route through this helper to avoid raw
 * snake_case leakage.
 */
export function humanizeContractorOnboardingStatus(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .toString()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
